/**
 * Categories Tab Component
 *
 * Tab content for managing raw product categories with hierarchical tree.
 */

import { useState, useMemo } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { CategoryTree } from '../CategoryTree';
import { CategoryFormModal } from '../CategoryFormModal';
import { useCategories } from '../../../raw-products/hooks/useCategories';
import type { CategoryTreeNode } from '@bstock/shared';

export function CategoriesTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryTreeNode | null>(null);
  const [parentIdForNew, setParentIdForNew] = useState<string | null>(null);

  const { data: categoriesTree, isLoading } = useCategories();

  // Filter tree if search query exists
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim() || !categoriesTree) return categoriesTree ?? [];

    const searchLower = searchQuery.toLowerCase();

    const filterNode = (node: CategoryTreeNode): CategoryTreeNode | null => {
      const matchesSearch =
        node.name.toLowerCase().includes(searchLower) ||
        node.code.toLowerCase().includes(searchLower);

      const filteredChildren = node.children
        ?.map(filterNode)
        .filter(Boolean) as CategoryTreeNode[] ?? [];

      if (matchesSearch || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }

      return null;
    };

    return (categoriesTree ?? [])
      .map(filterNode)
      .filter(Boolean) as CategoryTreeNode[];
  }, [categoriesTree, searchQuery]);

  const handleCreateRoot = () => {
    setParentIdForNew(null);
    setEditingCategory(null);
    setIsCreateModalOpen(true);
  };

  const handleCreateChild = (parentId: string) => {
    setParentIdForNew(parentId);
    setEditingCategory(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (category: CategoryTreeNode) => {
    setEditingCategory(category);
    setParentIdForNew(null);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingCategory(null);
    setParentIdForNew(null);
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between gap-4">
        <Button variant="primary" onClick={handleCreateRoot}>
          <Plus className="w-4 h-4" />
          Nouvelle categorie racine
        </Button>

        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="pl-10"
          />
        </div>
      </div>

      {/* Tree */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm">
        {isLoading ? (
          <div className="p-8 animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-neutral-100 rounded-lg" />
            ))}
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-neutral-500">
              {searchQuery ? 'Aucune categorie trouvee' : 'Aucune categorie creee'}
            </p>
            {!searchQuery && (
              <Button variant="primary" className="mt-4" onClick={handleCreateRoot}>
                <Plus className="w-4 h-4" />
                Creer ma premiere categorie
              </Button>
            )}
          </div>
        ) : (
          <CategoryTree
            nodes={filteredTree}
            onAddChild={handleCreateChild}
            onEdit={handleEdit}
          />
        )}
      </div>

      {/* Create/Edit Modal */}
      <CategoryFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        category={editingCategory}
        parentId={parentIdForNew}
      />
    </div>
  );
}
